/**
 * Date formatting utilities.
 *
 * Persian (fa) mode: Jalali only — "۱۴۰۵/۰۲/۲۸"
 * English (en) mode: both Gregorian AND Jalali — "2026-05-18 / 1405-02-28"
 *
 * This is a team decision documented in CLAUDE.md.
 */

import { format as formatGregorian } from 'date-fns';
import { format as formatJalali } from 'date-fns-jalali';

/**
 * Format a date according to the current language.
 *
 * @param {Date|string|number} date - Date object, ISO string, or timestamp
 * @param {'fa'|'en'} language - Current UI language
 * @param {object} [options]
 * @param {'short'|'long'|'datetime'} [options.style='short'] - Format style
 * @returns {string} Formatted date string
 *
 * @example
 *   formatDate(new Date(), 'fa')        // "۱۴۰۵/۰۲/۲۸"
 *   formatDate(new Date(), 'en')        // "2026-05-18 / 1405-02-28"
 *   formatDate(date, 'fa', { style: 'long' })   // "۲۸ اردیبهشت ۱۴۰۵"
 *   formatDate(date, 'en', { style: 'long' })   // "May 18, 2026 (Ordibehesht 28, 1405)"
 */
export function formatDate(date, language, options = {}) {
  const { style = 'short' } = options;
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  if (!d || isNaN(d.getTime())) return '';

  if (language === 'fa') {
    // Persian-only display: Jalali
    switch (style) {
      case 'long':
        return formatJalali(d, 'd MMMM yyyy');
      case 'datetime':
        return formatJalali(d, 'yyyy/MM/dd HH:mm');
      case 'short':
      default:
        return formatJalali(d, 'yyyy/MM/dd');
    }
  } else {
    // English mode: show both
    switch (style) {
      case 'long': {
        const g = formatGregorian(d, 'MMMM d, yyyy');
        const j = formatJalali(d, 'MMMM d, yyyy');
        return `${g} (${j})`;
      }
      case 'datetime': {
        const g = formatGregorian(d, 'yyyy-MM-dd HH:mm');
        const j = formatJalali(d, 'yyyy-MM-dd');
        return `${g} / ${j}`;
      }
      case 'short':
      default: {
        const g = formatGregorian(d, 'yyyy-MM-dd');
        const j = formatJalali(d, 'yyyy-MM-dd');
        return `${g} / ${j}`;
      }
    }
  }
}

/**
 * Format a time (no date).
 * @param {Date|string|number} date
 * @returns {string} "HH:mm" — same in both languages
 */
export function formatTime(date) {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  if (!d || isNaN(d.getTime())) return '';
  return formatGregorian(d, 'HH:mm');
}
